import { App } from '@capacitor/app'
import { CapacitorHttp, HttpOptions } from '@capacitor/core'
import { Dialog } from '@capacitor/dialog'
import { AppUpdate } from '@capawesome/capacitor-app-update'
import semver from 'semver'

import { appConfig } from 'services/config'
import { i18n } from '@/lib/translation'
import { isNative, isNativeAndroid } from '@/utils/utils'

const getVersion = async (): Promise<{
  enforce: boolean
  android: number
  iOS: string
}> =>
  await CapacitorHttp.get({
    url: `${appConfig.api}/version`,
    headers: {
      'Content-Type': 'application/json',
    },
  } as HttpOptions)
    .then((resp) => resp.data)
    .catch((error) => {
      console.error(error)
    })

const getCurrentAppVersion = async (): Promise<number | string> => {
  const result = await AppUpdate.getAppUpdateInfo()
  return result?.currentVersion
}

const getAvailableApp = async (): Promise<{
  enforce: boolean
  version: number | string
}> => {
  const result = await getVersion()
  return {
    enforce: result.enforce,
    version: isNativeAndroid() ? result.android : result.iOS,
  }
}

const openAppStore = async (): Promise<void> => {
  await AppUpdate.openAppStore()
}

const updateAppPrompt = async (isAppResumed?: boolean) => {
  try {
    const availableApp = await getAvailableApp()
    const currentVersion = await getCurrentAppVersion()

    if (!availableApp?.version || availableApp?.version === currentVersion)
      return

    const t = await i18n

    const isVersionStale = isNativeAndroid()
      ? availableApp.version > currentVersion
      : semver.gt(String(availableApp.version), String(currentVersion))

    if (isVersionStale) {
      if (availableApp.enforce) {
        await Dialog.alert({
          title: t('common:forceUpdate.majorTitle'),
          message: t('common:forceUpdate.message'),
          buttonTitle: t('common:forceUpdate.update'),
        })
        openAppStore()
        return
      } else {
        if (isAppResumed) return
        const result = await Dialog.confirm({
          title: t('common:forceUpdate.minorTitle'),
          message: t('common:forceUpdate.message'),
          okButtonTitle: t('common:forceUpdate.update'),
          cancelButtonTitle: t('common:forceUpdate.later'),
        })
        if (result.value) {
          openAppStore()
        }
        return
      }
    }
  } catch (error) {
    console.log(error)
  }
}

export const updateApp = () => {
  if (isNative()) {
    updateAppPrompt()
    App.addListener('resume', () => {
      updateAppPrompt(true)
    })
  }
}
